import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { C } from "./Disc";
export interface ExplodedPart {
  id: string;
  label: string;
  /** travel along the part's own axis, from assembled to exploded */
  delta: number;
  /** label anchor in the part's drawing coordinates (edge x, centre y) */
  anchorX: number;
  labelY: number;
  node: React.ReactNode;
}

/** Turns an upright drawing on its side so it explodes left-to-right. */
export interface HorizontalLayout {
  pivotX: number;
  pivotY: number;
  /** centre line x of the upright drawing */
  cx: number;
  topY: number;
  bottomY: number;
}
interface ExplodedViewProps {
  title: string;
  /** painter's order: bottom-most (or left-most) part first */
  parts: ExplodedPart[];
  active: boolean;
  width: number;
  height: number;
  /** vertical mode: x where labels start */
  labelX?: number;
  horizontal?: HorizontalLayout;
  className?: string;
}
const HOLD_EXPLODED = 4200;
const HOLD_ASSEMBLED = 1600;
const FIRST_EXPLODE = 700;
const textProps = {
  fill: C.ink,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 1,
  fontFamily: "'Plus Jakarta Sans', sans-serif"
};
export function ExplodedView({
  title,
  parts,
  active,
  width,
  height,
  labelX = 430,
  horizontal,
  className
}: ExplodedViewProps) {
  const reduce = useReducedMotion();
  const [exploded, setExploded] = useState(!!reduce);
  useEffect(() => {
    if (reduce) {
      setExploded(true);
      return;
    }
    setExploded(false);
    if (!active) return;
    let state = false;
    let timer = window.setTimeout(function tick() {
      state = !state;
      setExploded(state);
      timer = window.setTimeout(tick, state ? HOLD_EXPLODED : HOLD_ASSEMBLED);
    }, FIRST_EXPLODE);
    return () => window.clearTimeout(timer);
  }, [active, reduce]);
  const n = parts.length;
  const h = horizontal;
  const centerY = h ? h.pivotY + (h.cx - h.pivotX) : 0;
  return <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title} className={className ?? "h-auto w-full"} data-id="element-401" data-mp="3imur7spf91v7">
      {parts.map((p, i) => {
      const travel = h ? {
        x: exploded ? -p.delta : 0
      } : {
        y: exploded ? p.delta : 0
      };
      let labelEl: React.ReactNode;
      if (h) {
        const sx = h.pivotX - (p.labelY - h.pivotY);
        const r = p.anchorX - h.cx;
        const top = i % 2 === 1;
        const ay = top ? centerY - r : centerY + r;
        const ly = top ? h.topY : h.bottomY;
        labelEl = <>
              <circle cx={sx} cy={ay} r={3.5} fill={C.ink} data-id="element-402" data-mp="3402jinw6hzoj" />
              <line x1={sx} y1={ay} x2={sx} y2={ly} stroke={C.ink} strokeWidth={1.5} data-id="element-403" data-mp="382yy83illbn7" />
              <text x={sx} y={top ? ly - 10 : ly + 20} textAnchor="middle" {...textProps} data-id="element-404" data-mp="380w7fh9mfehf">
                {p.label.toUpperCase()}
              </text>
            </>;
      } else {
        labelEl = <>
              <circle cx={p.anchorX} cy={p.labelY} r={3.5} fill={C.ink} data-id="element-405" data-mp="1imzke941ay18" />
              <line x1={p.anchorX} y1={p.labelY} x2={labelX - 10} y2={p.labelY} stroke={C.ink} strokeWidth={1.5} data-id="element-406" data-mp="1b49uw24qnxsu" />
              <text x={labelX} y={p.labelY + 4.5} {...textProps} data-id="element-407" data-mp="16d1h6twdlq9v">
                {p.label.toUpperCase()}
              </text>
            </>;
      }
      return <motion.g key={p.id} initial={false} animate={travel} transition={{
        duration: reduce ? 0 : 0.6,
        ease: [0.65, 0, 0.35, 1],
        delay: reduce ? 0 : exploded ? i * 0.03 : (n - i) * 0.03
      }} data-id="element-408" data-mp="wfdp6bq5mwve" data-mpi={i}>
            {h ? <g transform={`rotate(90 ${h.pivotX} ${h.pivotY})`} data-id="element-409" data-mp="1k4f0c6qdgm4d">{p.node}</g> : p.node}
            <motion.g initial={false} animate={{
          opacity: exploded ? 1 : 0
        }} transition={{
          duration: 0.2,
          delay: exploded && !reduce ? 0.45 : 0
        }} data-id="element-410" data-mp="1g3cydi52lvyp">
              {labelEl}
            </motion.g>
          </motion.g>;
    })}
    </svg>;
}